import React, { useCallback, useState, memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { parse as csvParser } from 'csv-parse/browser/esm';

import { CenteredModal, useToast, Dropzone } from 'components';
import { Panel, panelsSelector } from 'store';
import { getFirstLinesOfFile } from 'lib/fileUtils';
import { ParticipantCsvMapping } from './ParticipantCsvMapping';
import { useConfirmUploadCsv, useCreateUploadCsv, CsvMapping } from '@app/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface Props {
  isOpen: boolean;
  onRequestClose: () => any;
  onUploadSuccess: (id: string) => any;
  panel: Panel;
}

const LINES_TO_READ = 4;
export const ImportCsvModal = memo(
  ({ isOpen, onRequestClose, panel, onUploadSuccess }: Props) => {
    const panels = useSelector(panelsSelector);
    const toast = useToast();
    const { t } = useTranslation();

    const { mutateAsync: createUpload } = useCreateUploadCsv(panel.id);
    const { mutateAsync: confirmUpload } = useConfirmUploadCsv();

    const [firstLines, setFirstLines] = useState<string[][]>([]);
    const [file, setFile] = useState<File | null>(null);

    useEffect(() => {
      if (!isOpen) {
        return;
      }
      setFirstLines([]);
      setFile(null);
    }, [isOpen, setFirstLines, setFile]);

    const onDrop = useCallback(
      async (acceptedFiles: File[]) => {
        if (!acceptedFiles.length) {
          return;
        }
        const file = acceptedFiles[0];
        setFile(file);

        const res = await getFirstLinesOfFile(file, LINES_TO_READ);
        csvParser(res, {}, (err?: Error, output?: string[][]) => {
          if (err) {
            toast.exception(err);
            return;
          }
          if (output) {
            setFirstLines(output);
          }
        });
      },
      [setFile, setFirstLines, toast],
    );

    const newSubmit = useCallback(
      async (values: { mapping: CsvMapping; haveConsent: boolean }) => {
        try {
          const csvImport = await createUpload({
            mapping: values.mapping,
            filename: file?.name,
          });
          await axios({
            url: csvImport.uploadUrl,
            method: 'PUT',
            data: file,
            headers: { 'Content-Type': 'txt/csv' },
            maxContentLength: 100 * 1024 * 1024 * 1024,
            timeout: 30 * 60 * 1000, //30mins
          });
          await confirmUpload(csvImport.id);
          onUploadSuccess(csvImport.id);
          onRequestClose();
        } catch (error) {
          toast.exception(error);
        }
      },
      [createUpload, confirmUpload, file, toast],
    );

    return (
      <CenteredModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        title={
          !firstLines.length
            ? t('participants.list.importCsv.upload.title', {
                name: panel.name,
                count: panels.length,
                filename: file ? file.name : '',
              })
            : t('participants.list.importCsv.mapping.title', {
                name: panel.name,
                count: panels.length,
                filename: file ? file.name : '',
              })
        }
      >
        {!firstLines.length ? (
          <Dropzone
            onDrop={onDrop}
            label={t('participants.list.importCsv.upload.dropzoneLabel')}
            format="csv"
          />
        ) : (
          <ParticipantCsvMapping
            firstLines={firstLines}
            onSubmit={newSubmit}
            panelName={panels.length > 1 ? panel.name : ''}
          />
        )}
      </CenteredModal>
    );
  },
);
